// src/portal/tenant/TenantLogin.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getOrganizationSlug } from '../../utils/slug';
import Navbar from '../../pages/home/Navbar';
import SimpleFooter from '../../pages/home/SimpleFooter';
import {
  ArrowRightIcon,
  ChevronDownIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  BellAlertIcon,
  CloudArrowUpIcon,
  ListBulletIcon,
  CalendarDaysIcon,
  LockClosedIcon,
  SparklesIcon,
  UserIcon } from
'@heroicons/react/24/outline';

const TenantLogin = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const { login } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    setLoaded(true);
    window.scrollTo(0, 0);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const slug = getOrganizationSlug();
      const { data } = await api.post('/portal/auth/tenant/login', {
        email,
        password,
        slug // Extra field for SaaS scoped login
      });

      if (data.success) {
        login(data.user, data.token);
        toast.success(t('tenant_signin_success') || 'Access Granted. Welcome home.');
        navigate('/portal/tenant/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('login_failed') || 'Identity verification failed');
    } finally {
      setLoading(false);
    }
  };

  const coreFeatures = [
  {
    title: "Balance & Rent",
    desc: "View your current amount due and pay instantly using Stripe.",
    icon: CreditCardIcon
  },
  {
    title: "Repair Requests",
    desc: "Send maintenance reports with photos directly to our engineers.",
    icon: WrenchScrewdriverIcon
  },
  {
    title: "Lease Signing",
    desc: "Sign and download your rental agreements safely from any device.",
    icon: DocumentTextIcon
  }];


  const detailedFunctions = [
  { title: "Payment History", desc: "See every rent payment and service fee in one clear log.", icon: ListBulletIcon },
  { title: "Smart Statements", desc: "Download itemized PDF/CSV reports for your own records.", icon: CloudArrowUpIcon },
  { title: "Building News", desc: "Stay updated with real-time news and alerts from your building.", icon: BellAlertIcon },
  { title: "Secure Vault", desc: "Safe storage for your ID, insurance, and lease documents.", icon: LockClosedIcon },
  { title: "One-Tap Pay", desc: "Quickly clear balances using saved cards via Stripe checkout.", icon: SparklesIcon },
  { title: "Live Tracking", desc: "See exactly when an engineer is assigned to your repair.", icon: CalendarDaysIcon },
  { title: "Lease Management", desc: "Review your contract terms, start dates, and end dates anytime.", icon: DocumentTextIcon },
  { title: "Emergency Contact", desc: "Quick access to building support and maintenance priority.", icon: ShieldCheckIcon },
  { title: "Account Security", desc: "Manage 2FA, session logs, and personal security settings.", icon: UserIcon }];


  return (
    <div className={`min-h-screen transition-colors duration-1000 ${isDarkMode ? 'bg-[#050505] text-white' : 'bg-[#fafafa] text-zinc-900'}`}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap');
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600&display=swap');
                
                body { font-family: 'Inter', sans-serif; }
                .h-serif { font-family: 'Instrument Serif', serif; }
                
                .glass-input {
                    background: ${isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'};
                    border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
                    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .glass-input:focus {
                    background: ${isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(255,255,255,0.95)'};
                    border-color: #ef4444;
                    transform: translateY(-1px);
                }
                .action-button {
                    background: ${isDarkMode ? '#fff' : '#000'};
                    color: ${isDarkMode ? '#000' : '#fff'};
                    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .action-button:hover {
                    opacity: 0.9;
                    transform: scale(1.01);
                }
                .grid-border { 
                    border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}; 
                    transition: all 0.5s ease;
                }
                .grid-border:hover {
                    background: ${isDarkMode ? 'rgba(185, 28, 28, 0.03)' : 'rgba(185, 28, 28, 0.02)'};
                }
            `}</style>

            <Navbar />

            <div className="flex flex-col lg:flex-row min-h-screen">
                {/* LEFT: LOGIN */}
                <div className="w-full lg:w-[45%] flex flex-col justify-center px-8 lg:px-24 py-32 bg-inherit">
                    <div className={`max-w-[420px] w-full mx-auto transition-all duration-1000 ${loaded ? 'opacity-100' : 'opacity-0 translate-y-4'}`}>
                        <div className="mb-12">
                            <h2 className="text-[10px] font-bold uppercase tracking-[0.4em] text-red-600 mb-6 flex items-center gap-2">
                                <LockClosedIcon className="w-3 h-3" /> {t("secure_access")}
                            </h2>
                            <h1 className="h-serif text-6xl font-light tracking-tight leading-none mb-6">{t("hello_1")} <span className="italic">{t("again")}</span></h1>
                            <p className="text-sm font-light opacity-85 leading-relaxed">{t("enter_your_details_to_manage_your_home_a")}</p>
                        </div>

                        <form className="space-y-8" onSubmit={handleLogin}>
                            <div className="space-y-2">
                                <label className="text-[9px] font-bold uppercase tracking-widest opacity-40 ml-1">{t("your_email")}</label>
                                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-6 py-5 rounded-2xl outline-none glass-input text-sm font-light"
                  placeholder="your@email.com" />
                
                            </div>
                            <div className="space-y-2">
                                <label className="text-[9px] font-bold uppercase tracking-widest opacity-40 ml-1">{t("your_password")}</label>
                                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-6 py-5 rounded-2xl outline-none glass-input text-sm tracking-[0.3em]"
                  placeholder="••••••••" />
                
                            </div>

                            <button
                type="submit"
                disabled={loading}
                className="w-full h-16 rounded-full action-button font-black text-[11px] uppercase tracking-[0.2em] flex items-center justify-center gap-3 active:scale-[0.98] mt-4">
                
                                {loading ? 'Logging in...' : 'Sign In'}
                                <ArrowRightIcon className="w-4 h-4" />
                            </button>
                        </form>

                        <div className="mt-12 pt-8 border-t border-red-600/5">
                            <Link to="/portal/owner/login" className="text-[9px] font-bold uppercase tracking-[0.3em] opacity-40 hover:opacity-100 transition-opacity hover:text-red-600">{t("switch_to_owner_login")}</Link>
                        </div>
                    </div>
                </div>

                {/* RIGHT: STORY */}
                <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-black">
                    <img
            src="https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?q=80&w=2070&auto=format&fit=crop"
            alt="Interior"
            className={`absolute inset-0 w-full h-full object-cover opacity-60 transition-transform duration-[20s] ease-out ${loaded ? 'scale-110' : 'scale-100'}`} />
          
                    <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />
                    
                    <div className="absolute bottom-24 left-24 right-24 space-y-12">
                        <div className="space-y-4">
                            <div className="h-[2px] w-12 bg-red-600" />
                            <h3 className="h-serif text-6xl text-white font-light leading-tight">{t("everything_in_one_place")}</h3>
                        </div>
                        
                        <div className="grid grid-cols-1 gap-8">
                            {coreFeatures.map((f, i) =>
              <div key={i} className="flex items-start gap-4">
                                    <div className="mt-1">
                                        <f.icon className="w-5 h-5 text-red-600" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <h4 className="text-[11px] font-bold text-white uppercase tracking-[0.2em] mb-1">{f.title}</h4>
                                        <p className="text-[11px] text-white/75 font-light max-w-xs leading-relaxed">{f.desc}</p>
                                    </div>
                                </div>
              )}
                        </div>
                    </div>
                </div>
            </div>

            {/* MORE FEATURES BELOW */}
            <section className="py-48 px-8 lg:px-24 border-t border-white/5 bg-inherit relative z-10">
                <div className="">
                    <div className="mb-24 space-y-4">
                        <h2 className="text-red-600 text-[10px] font-bold uppercase tracking-[0.4em]">{t("tools_for_you")}</h2>
                        <h3 className="h-serif text-6xl lg:text-7xl leading-none tracking-tight">{t("everything_you_need")} <br /><span className="opacity-70">{t("to_manage_your_home")}</span></h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 -mx-[1px]">
                        {detailedFunctions.map((item, i) =>
            <div key={i} className="grid-border p-10 lg:p-12 space-y-8 group">
                                <div className="flex items-center justify-between">
                                    <div className="text-red-700/40 group-hover:text-red-700 transition-colors">
                                        <item.icon className="w-6 h-6" strokeWidth={1} />
                                    </div>
                                    <div className={`text-[10px] font-mono ${isDarkMode ? 'text-white/10' : 'text-black/10'}`}>
                                        0{i + 1}
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <h4 className={`text-xl font-light tracking-tight ${isDarkMode ? 'text-white' : 'text-black'} group-hover:text-red-700 transition-colors`} style={{ fontFamily: "'Instrument Serif', serif" }}>{item.title}</h4>
                                    <p className={`text-[13px] font-light leading-relaxed ${isDarkMode ? 'text-white/75' : 'text-zinc-900/60'} group-hover:text-inherit transition-all`}>{item.desc}</p>
                                </div>
                            </div>
            )}
                    </div>

                    <div className="mt-32 pt-16 border-t border-white/5 flex flex-col items-center text-center space-y-8">
                        <ShieldCheckIcon className="w-12 h-12 text-zinc-300" strokeWidth={0.5} />
                        <h4 className="h-serif text-4xl font-light opacity-75 leading-tight">{t("safe_private_reliable")}</h4>
                        <p className="text-sm font-light opacity-70 max-w-xl">{t("we_use_professional_grade_encryption_to")}</p>
                    </div>
                </div>
            </section>
            <SimpleFooter />
        </div>);

};

export default TenantLogin;